import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useNotificationInbox from '../../hooks/useNotificationInbox';
import NotificationInboxShell from '../Notifications/NotificationInboxShell';
import BookingRequestDetailModal from './BookingRequestDetailModal';
import {
  acceptTutorBookingRequest,
  declineTutorBookingRequest,
} from '../../services/tutorBookingService';
import { showSystemMessage } from '../../utils/systemMessage';

const SESSION_TYPES = ['booking_confirmation', 'booking_request', 'cancellation', 'session_reminder'];

const TutorNotificationsPage = () => {
  const navigate = useNavigate();
  const inbox = useNotificationInbox('tutor');
  const [detailBookingId, setDetailBookingId] = useState(null);
  const [bookingActionLoading, setBookingActionLoading] = useState(false);

  const handleBookingAction = async (action, successMessage) => {
    if (!detailBookingId) return false;
    try {
      setBookingActionLoading(true);
      await action(detailBookingId);
      showSystemMessage({ message: successMessage, type: 'success' });
      return true;
    } catch (error) {
      showSystemMessage({
        message: error.message || 'Unable to update the booking request.',
        type: 'error',
      });
      return false;
    } finally {
      setBookingActionLoading(false);
    }
  };

  const openMessages = async () => {
    const selected = inbox.selected;
    if (!selected) return;
    await inbox.markItemRead(selected);

    const conversationId = selected.metadata?.conversationId;
    if (conversationId) {
      navigate(`/messages?conversation=${conversationId}`);
      return;
    }
    const messageRequestId = selected.metadata?.messageRequestId;
    if (messageRequestId) {
      navigate(`/messages?request=${messageRequestId}`);
      return;
    }
    navigate('/messages');
  };

  return (
    <>
      <NotificationInboxShell
        subtitle="Review session reminders, booking confirmations, cancellation alerts, messages from learners, and support notices in one place."
        inbox={inbox}
        getDetailHeading={(selected) => {
          if (selected.type === 'booking_confirmation') return 'A session has been confirmed';
          if (selected.type === 'booking_request') return 'A learner sent a booking request';
          return selected.title;
        }}
        getDetailBody={(selected) => selected.notification || selected.preview}
        renderDetailActions={(selected) => (
          <div className="mt-6 flex flex-wrap gap-3">
            {selected.type === 'booking_request' && selected.metadata?.bookingId && (
              <button
                type="button"
                onClick={() => setDetailBookingId(selected.metadata.bookingId)}
                className="rounded-lg bg-brand-medium-blue px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-dark-blue"
              >
                View Request Details
              </button>
            )}
            {SESSION_TYPES.includes(selected.type) && selected.type !== 'booking_request' && (
              <Link
                to="/tutor/session-history"
                className="rounded-lg bg-brand-medium-blue px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-dark-blue"
              >
                View Session
              </Link>
            )}
            {selected.type === 'message' && (
              <button
                type="button"
                onClick={openMessages}
                className="rounded-lg border-2 border-brand-medium-blue px-5 py-2.5 text-sm font-medium text-brand-medium-blue hover:bg-brand-light-blue/20"
              >
                Open Message
              </button>
            )}
          </div>
        )}
      />

      <BookingRequestDetailModal
        open={Boolean(detailBookingId)}
        bookingId={detailBookingId}
        onAccept={() =>
          handleBookingAction(
            acceptTutorBookingRequest,
            'Booking request accepted successfully!',
          )
        }
        onDecline={() =>
          handleBookingAction(
            declineTutorBookingRequest,
            'Booking request declined successfully!',
          )
        }
        actionLoading={bookingActionLoading}
        onClose={() => setDetailBookingId(null)}
      />
    </>
  );
};

export default TutorNotificationsPage;
